import type { PlayerEvents } from '../../player/events';
import { historyLogger, secondsToMs, type HistoryLogger } from './logger';
import { historySessions, type HistorySessionTracker } from './sessionTracker';

/** Slider drags log only the settled speed (`_playbackSpeedHistoryDebounceDelay`). */
export const SPEED_HISTORY_DEBOUNCE_MS = 700;
/** Playback that starts more than 2 s in is a resume (`player_provider.dart:2370`). */
const RESUME_THRESHOLD_S = 2;

export interface PlayerHistoryOptions {
  logger?: HistoryLogger;
  sessions?: HistorySessionTracker;
  speedDebounceMs?: number;
}

/**
 * Logs the player's history events the way PlayerProvider does: play/pause
 * transitions with session ids, seeks, skips, chapter navigation, speed and
 * sleep timer changes. Returns a disconnect function.
 */
export function connectPlayerHistory(events: PlayerEvents, opts: PlayerHistoryOptions = {}): () => void {
  const logger = opts.logger ?? historyLogger;
  const sessions = opts.sessions ?? historySessions;
  const debounceMs = opts.speedDebounceMs ?? SPEED_HISTORY_DEBOUNCE_MS;

  let speedBurst: { bookId: string; from: number; to: number; timer: ReturnType<typeof setTimeout> } | null = null;

  const logSpeed = (bookId: string, from: number, to: number) => {
    if (Math.abs(to - from) < 0.001) return;
    void logger.logPlaybackSpeedChanged({ bookId, speed: to, sessionId: sessions.sessionIdFor(bookId) });
  };

  const flushSpeed = () => {
    if (!speedBurst) return;
    const { bookId, from, to, timer } = speedBurst;
    clearTimeout(timer);
    speedBurst = null;
    logSpeed(bookId, from, to);
  };

  const offs = [
    events.on('sessionStarted', () => sessions.clear()),

    events.on('play', ({ itemId, position }) => {
      void logger.logPlaybackStarted({
        bookId: itemId,
        sessionId: sessions.ensureSessionFor(itemId),
        positionMs: secondsToMs(position),
        resumed: position > RESUME_THRESHOLD_S,
      });
    }),

    events.on('pause', ({ itemId, position, stopped }) => {
      void logger.logPlaybackPaused({ bookId: itemId, sessionId: sessions.sessionIdFor(itemId), positionMs: secondsToMs(position), stopped });
      sessions.markPlaybackEnded();
    }),

    events.on('seek', ({ itemId, from, to, kind, chapterIndex, chapter }) => {
      const sessionId = sessions.sessionIdFor(itemId);
      const fromMs = secondsToMs(from);
      const toMs = secondsToMs(to);
      switch (kind) {
        case 'skipBack':
          void logger.logSeek({ bookId: itemId, fromMs, toMs, sessionId, type: 'jumpBackward' });
          return;
        case 'skipForward':
          void logger.logSeek({ bookId: itemId, fromMs, toMs, sessionId, type: 'jumpForward' });
          return;
        case 'chapterSelect':
        case 'chapterNext':
        case 'chapterPrevious':
          if (chapter) {
            void logger.logChapterNavigation({
              bookId: itemId,
              type: kind === 'chapterSelect' ? 'chapterSelected' : kind,
              chapterId: String(chapter.id),
              chapterTitle: chapter.title,
              chapterIndex,
              targetMs: toMs,
              sessionId,
            });
            return;
          }
          void logger.logSeek({ bookId: itemId, fromMs, toMs, sessionId });
          return;
        case 'smartRewind':
          void logger.logSeek({ bookId: itemId, fromMs, toMs, sessionId, reason: 'Smart rewind' });
          return;
        case 'serverSync':
          void logger.logSeek({ bookId: itemId, fromMs, toMs, sessionId, reason: 'Progress from another device' });
          return;
        default:
          void logger.logSeek({ bookId: itemId, fromMs, toMs, sessionId });
      }
    }),

    events.on('rateChanged', ({ itemId, from, to, transient, silent }) => {
      // Scope toggles push the book's speed without a history entry (setPlaybackSpeedScope).
      if (!itemId || silent) return;
      if (speedBurst && speedBurst.bookId !== itemId) flushSpeed();
      if (transient) {
        const start = speedBurst?.from ?? from;
        if (speedBurst) clearTimeout(speedBurst.timer);
        speedBurst = { bookId: itemId, from: start, to, timer: setTimeout(flushSpeed, debounceMs) };
        return;
      }
      if (speedBurst) {
        speedBurst.to = to;
        flushSpeed();
        return;
      }
      logSpeed(itemId, from, to);
    }),

    events.on('sleepTimerSet', ({ itemId, durationMs }) => {
      if (itemId) void logger.logSleepTimerSet({ bookId: itemId, durationMs, sessionId: sessions.sessionIdFor(itemId) });
    }),

    events.on('sleepTimerCancelled', ({ itemId, reason }) => {
      if (!itemId || reason === 'finished') return;
      const sessionId = sessions.sessionIdFor(itemId);
      if (reason === 'expired') void logger.logSleepTimerExpired({ bookId: itemId, sessionId });
      else void logger.logSleepTimerCancelled({ bookId: itemId, sessionId });
    }),

    events.on('sleepTimerFired', ({ itemId }) => {
      if (itemId) void logger.logSleepTimerExpired({ bookId: itemId, sessionId: sessions.sessionIdFor(itemId) });
    }),
  ];

  return () => {
    flushSpeed();
    offs.forEach((off) => off());
  };
}
